import React from 'react';
import PropTypes from 'prop-types';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/ext/dhtmlxgantt_undo.js';
import 'dhtmlx-gantt/codebase/ext/dhtmlxgantt_marker.js';
import 'dhtmlx-gantt/codebase/ext/dhtmlxgantt_tooltip.js';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import 'dhtmlx-gantt/codebase/api.js';
import { GANTT_ZOOM_LEVELS, CORE_STATUSES, GANTT_EXPORT_SERVICE_URL, MOBILE_WINDOW_BREAKPOINT } from '../../../constants/constants';
import { TruncateText } from '../../../helpers/strings';
import Moment from 'moment';
import './GanttChart.scss';
import $ from 'jquery';

const GANTT_DRAG_MODES = {
    Move: 'move',
    Resize: 'resize',
};
// https://docs.dhtmlx.com/gantt/api__refs__gantt.html
export class GanttChart extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            updatedTask: null,
        };
    }

    // sets up all of the config of the gantt chart
    componentDidMount = async () => {
        gantt.config.xml_date = '%Y-%m-%d %H:%i';
        gantt.config.show_progress = false;
        gantt.config.show_quick_info = false;
        gantt.config.show_markers = true;
        gantt.config.start_on_monday = this.props.startOnMonday;

        // set inital scale level
        this.setScaleConfig(GANTT_ZOOM_LEVELS.Month.value);

        const isMobile = window.innerWidth < MOBILE_WINDOW_BREAKPOINT;
        if (isMobile) {
            gantt.config.columns = [
                {
                    name: 'text',
                    label: 'Task Name',
                    tree: true,
                    width: 50,
                },
            ];
            gantt.config.grid_width = 50;
        } else {
            gantt.config.columns = this.props.ganttColumns || [
                {
                    name: 'text',
                    label: 'Task Name',
                    align: 'left',
                    tree: true,
                    width: 350,
                },
            ];
            gantt.config.grid_width = 350;
        }

        gantt.templates.grid_row_class = function (start, end, event) {
            //console.log(event);
            const task = event.eh_task;
            const classes = [];

            if (task && task.isMilestone) {
                classes.push('eh-milestone-event'); // how milestones look on the grid
            }

            if (typeof event.id != 'string' || event.id.indexOf('Project_') == -1) {
                classes.push('eh-non-project-event'); // how things other than projects look on the grid
            }

            return classes.length ? classes.join(' ') : '';
        };

        // added additional classes to the task elements on the grid based on status
        gantt.templates.task_class = function (start, end, event) {
            let statusClass = '';
            const task = event.eh_task;

            // are we styling the location, project or task?
            if (task) {
                if (event.eh_showStatus) {
                    // change the border of the event to the color of the status
                    if (task.coreStatusId === CORE_STATUSES.Complete) {
                        statusClass = 'eh-event-complete';
                    } else if (task.coreStatusId === CORE_STATUSES.NotApplicable) {
                        statusClass = 'eh-event-notdue';
                    } else {
                        const targetDate = Moment.utc(task.targetDate);
                        if (Moment().isAfter(targetDate.add(1, 'days'))) {
                            statusClass = 'eh-event-pastdue';
                        } else {
                            if (task.coreStatusId === CORE_STATUSES.NotStarted) {
                                statusClass = 'eh-event-notdue';
                            } else {
                                statusClass = 'eh-event-inprogress';
                            }
                        }
                    }
                    return 'eh-task-event ' + statusClass;
                }
            } else if (event.id.indexOf('Location_') != -1) {
                // how locations look on the grid
                return 'eh-location-event';
            } else if (event.id.indexOf('Project_') != -1) {
                // how projects look on the grid
                return 'eh-project-event';
            }
        };

        // shows tooltip on hover with full task name
        gantt.templates.tooltip_text = function (start, end, event) {
            const task = event.eh_task;
            return task ? task.name : event.text;
        };

        // sets the bg color in the grid for an event
        gantt.templates.task_row_class = function (start, end, event) {
            const task = event.eh_task;
            // we only want to change the bg color of the location events
            if (!task && event.id.indexOf('Location_') != -1) {
                return 'eh-location-row';
            }
        };

        gantt.templates.rightside_text = function (start, end, event) {
            const truncLen = 50;
            const task = event.eh_task;
            if (task) {
                // if the task has a start date only display the text on the side for short durations
                if (task.startDate) {
                    if (event.duration <= 3) {
                        return TruncateText(task.name, truncLen);
                    } else {
                        return null;
                    }
                } else {
                    // for tasks with no start date, always display the task name on the right
                    return TruncateText(task.name, truncLen);
                }
            } else {
                return null;
            }
        };

        gantt.init(this.ganttContainer);
        gantt.parse(this.props.chartData);
        gantt.renderMarkers();

        // the functions attached below will not recognize this.props so declaring these here are necessary
        const props = this.props;
        const onMoveTask = this.props.onMoveTask;
        const onResizeTask = this.props.onResizeTask;

        // when the user double-clicks on the task we want to show the task detail drawer
        gantt.attachEvent('onTaskDblClick', (id) => {
            props.onSelectTask(parseInt(id));
        });

        // if there is no move event callback then disable movement
        gantt.config.drag_move = this.props.onMoveTask ? true : false;

        // tracks the task that is moving or being resized
        gantt.attachEvent('onTaskDrag', (id, mode, task) => {
            this.setState({ updatedTask: task });
        });

        // fires when the user releases the mouse after moving or resizing a task
        gantt.attachEvent('onAfterTaskDrag', (taskId, mode) => {
            // we don't care about the times so create new dates with same time so we can properly calculate diffs
            let originalStart = null;
            if (!this.state.updatedTask) {
                return;
            }
            if (this.state.updatedTask.eh_task.startDate) {
                originalStart = Moment.utc(this.state.updatedTask.eh_task.startDate);
                originalStart = Moment([originalStart.year(), originalStart.month(), originalStart.date(), 0, 0, 0, 0]);
            }

            let originalTarget = Moment.utc(this.state.updatedTask.eh_task.targetDate);
            originalTarget = Moment([originalTarget.year(), originalTarget.month(), originalTarget.date(), 0, 0, 0, 0]);

            let updatedStart = Moment.utc(this.state.updatedTask.start_date);
            updatedStart = Moment([updatedStart.year(), updatedStart.month(), updatedStart.date(), 0, 0, 0, 0]);

            // remember to account for the +1 day that was added to the target dates (on the chart task only) so they extend through the target date
            let updatedTarget = Moment.utc(this.state.updatedTask.end_date).subtract(1, 'days');
            updatedTarget = Moment([updatedTarget.year(), updatedTarget.month(), updatedTarget.date(), 0, 0, 0, 0]);

            const dateToDiff = originalStart ? originalStart : originalTarget;
            const deltaDays = Moment.duration(updatedStart.diff(dateToDiff)).as('days');

            if (mode === GANTT_DRAG_MODES.Move) {
                onMoveTask(parseInt(taskId), deltaDays);
            } else if (mode === GANTT_DRAG_MODES.Resize) {
                onResizeTask(parseInt(taskId), originalStart, originalTarget, updatedStart, updatedTarget);
            }
        });

        if (this.props.isTaskDependenciesActive === false) {
            //remove the ability to remove contingent links here for now
            gantt.config.drag_links = false;
            gantt.attachEvent('onLinkDblClick', () => {
                return false;
            });
        } else {
            gantt.config.drag_links = true;
            // otherwise attach additional events
            const onRemoveLink = this.props.onRemoveLink;
            const onAddLink = this.props.onAddLink;
            gantt.attachEvent('onAfterLinkDelete', (linkId, linkObj) => {
                onRemoveLink(linkObj.source, linkObj.target);
            });

            gantt.attachEvent('onAfterLinkAdd', (linkId, linkObj) => {
                onAddLink(linkObj.source, linkObj.target, linkObj.type);
            });
        }

        if (this.props.isStartDateActive === false) {
            gantt.config.drag_resize = false;
        }
    };

    setScaleConfig = (level) => {
        //https://docs.dhtmlx.com/gantt/desktop__zooming.html
        const props = this.props;
        switch (level) {
            case GANTT_ZOOM_LEVELS.Month.value:
                gantt.config.scales = [{ unit: 'month', step: 1, format: '%M %Y' }];
                gantt.config.scale_height = 50;
                break;
            case GANTT_ZOOM_LEVELS.Week.value:
                gantt.config.scales = [
                    {
                        unit: 'week',
                        step: 1,
                        format: function (date) {
                            // from what I can tell the date is a random day in the week so we need to find the monday or sunday of that week and format it
                            let daysToAdjust = 0;
                            let currentDay = Moment.utc(date);
                            if (props.startOnMonday) {
                                daysToAdjust = 1 - currentDay.day();
                            } else {
                                daysToAdjust = currentDay.day();
                            }

                            const retval = currentDay.add(daysToAdjust, 'days').format('D MMM');
                            return retval;
                        },
                    },
                ];
                gantt.config.scale_height = 50;
                break;
            case GANTT_ZOOM_LEVELS.Day.value:
                gantt.config.scales = [{ unit: 'day', step: 1, format: '%d %M' }];
                gantt.config.scale_height = 27;
                break;
        }
    };

    componentDidUpdate(prevProps) {
        if (this.props.lastSearchTime && this.props.lastSearchTime !== prevProps.lastSearchTime) {
            let scroll = gantt.getScrollState();
            gantt.clearAll();
            gantt.parse(this.props.chartData);
            gantt.refreshData();
            gantt.scrollTo(scroll.x, scroll.y);
        }

        if (this.props.zoomLevel !== prevProps.zoomLevel) {
            this.setScaleConfig(this.props.zoomLevel);
        }

        const styles = [];
        for (const el in document.styleSheets) {
            try {
                const rules = document.styleSheets[el].cssRules;
                for (const rule in rules) {
                    styles.push(rules[rule].cssText);
                }
            } catch (e) {
                $.noop();
            }
        }

        if (this.props.exportToPDF !== prevProps.exportToPDF) {
            gantt.exportToPDF({
                server: GANTT_EXPORT_SERVICE_URL,
                raw: true,
                header: '<style>' + styles.join(' ') + '</style>',
            });
        }

        if (this.props.exportToPNG !== prevProps.exportToPNG) {
            gantt.exportToPNG({
                server: GANTT_EXPORT_SERVICE_URL,
                raw: true,
                header: '<style>' + styles.join(' ') + '</style>',
            });
        }

        // Did user toggle the critical path option?
        if (this.props.showCriticalPath !== prevProps.showCriticalPath) {
            gantt.config.highlight_critical_path = this.props.showCriticalPath;
            gantt.render();
        }
    }

    render() {
        return (
            <div
                ref={(input) => {
                    this.ganttContainer = input;
                }}
                style={{ width: '100%', height: this.props.height + 'px' }}></div>
        );
    }
}

GanttChart.propTypes = {
    chartData: PropTypes.object.isRequired,
    height: PropTypes.number,
    onSelectTask: PropTypes.func.isRequired,
    onMoveTask: PropTypes.func,
    onResizeTask: PropTypes.func,
    onRemoveLink: PropTypes.func,
    onAddLink: PropTypes.func,
    lastSearchTime: PropTypes.instanceOf(Date),
    zoomLevel: PropTypes.number.isRequired,
    exportToPDF: PropTypes.bool.isRequired,
    exportToPNG: PropTypes.bool,
    startOnMonday: PropTypes.bool.isRequired,
    isTaskDependenciesActive: PropTypes.bool.isRequired,
    isStartDateActive: PropTypes.bool.isRequired,
    showCriticalPath: PropTypes.bool.isRequired,
    ganttColumns: PropTypes.array,
};

export default GanttChart;
